import { of as observableOf, empty as observableEmpty } from 'rxjs';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import * as moment from 'moment';
import { ToasterService } from 'angular2-toaster';
import { formatNumber } from 'libphonenumber-js';
import {
  switchMap,
  debounceTime,
  tap,
  pairwise,
  startWith,
} from 'rxjs/operators';

import { PrintService } from '../../../services/print.service';
import { CountriesService } from '../../../services/countries.service';
import { AppConfigService } from '../../../services/app-config.service';
import { CreditCardStorageSettingsService } from 'app/services/credit-card-storage-settings.service';
import { FirebaseService } from 'app/services/firebase.service';
import { ShowStoryService } from 'app/+stories/show/show.service';
import { FacebookPixelService } from 'app/services/facebook-pixel.service';

@Component({
  selector: 'print-summary-step',
  styleUrls: [
    '../../wizard/wizard.component.scss',
    './printSummaryStep.component.scss',
  ],
  templateUrl: './printSummaryStep.component.html',
})
export class PrintSummaryStepComponent implements OnInit {
  @Input() step;
  @Input() totalSteps;
  @Input() next;
  @Input() prev;
  @Input() setStep;
  public isCouponInputOpen = false;
  public discount = {
    code: '',
    error: '',
  };
  storyId;
  storyCover;
  isLoading: boolean;
  isProcessing: boolean;
  isDiscountLoading: boolean;
  quantityControl = new FormControl(1);
  isQuantityLoading = false;

  constructor(
    public printService: PrintService,
    private countriesService: CountriesService,
    private toasterService: ToasterService,
    private firebaseService: FirebaseService,
    private appConfigService: AppConfigService,
    private activatedRoute: ActivatedRoute,
    private showStoryService: ShowStoryService,
    private creditCardStorageSettingsService: CreditCardStorageSettingsService,
    private facebookPixelService: FacebookPixelService,
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.storyId = this.activatedRoute.snapshot.params.id;
    this.printService.save().subscribe(data => {
      this.quantityControl.setValue(data.quantity, { emitEvent: false });
      if (this.printService.specs.discount_code) {
        this.printService.save().subscribe();
      }
      this.isLoading = false;
    });

    this.quantityControl.valueChanges
      .pipe(
        startWith(this.quantityControl.value),
        pairwise(),
        switchMap(([oldValue, newValue]) => {
          let quantity = +`${newValue}`.replace(/\D/g, '');

          if (+quantity <= 0) {
            quantity = 1;
          }

          const max = this.appConfigService.config.max_book_order_quantity;

          if (+quantity > max) {
            quantity = max;
          }

          this.quantityControl.setValue(quantity, { emitEvent: false });

          if (oldValue === quantity) {
            return observableEmpty();
          }

          return observableOf(quantity);
        }),
        debounceTime(1000),
        tap(() => {
          this.quantityControl.disable({ emitEvent: false });
          this.isQuantityLoading = true;
        }),
        switchMap(value => this.printService.changeQuantity(value))
      )
      .subscribe(
        (order: any) => {
          this.quantityControl.enable({ emitEvent: false });
          this.isQuantityLoading = false;
          this.quantityControl.setValue(order.quantity, { emitEvent: false });
        },
        () => {
          this.quantityControl.enable({ emitEvent: false });
          this.isQuantityLoading = false;
          this.quantityControl.setValue(this.order.quantity, {
            emitEvent: false,
          });
        }
      );

    this.showStoryService.getStoryCover(this.storyId).subscribe(cover => {
      this.storyCover = cover;
    });
  }

  get order() {
    return this.printService.printOrder;
  }

  decrementQuantity() {
    this.quantityControl.setValue(+this.quantityControl.value - 1);
  }

  incrementQuantity() {
    this.quantityControl.setValue(+this.quantityControl.value + 1);
  }

  toggleCouponInput() {
    this.isCouponInputOpen = !this.isCouponInputOpen;
  }

  applyDiscount() {
    this.isDiscountLoading = true;
    this.printService.applyDiscount(this.discount.code).subscribe(
      order => {
        this.isDiscountLoading = false;
        this.discount.error = '';
        this.toggleCouponInput();
        this.firebaseService.logEvent('checkout_discount_enter', {
          print_order_id: this.order.id,
          story_id: this.printService.storyId,
          project_id: this.printService.projectId,
          discount_code: this.discount.code,
        });
      },
      data => {
        this.discount.error = data.error.discount
          ? data.error.discount[0]
          : data.error.discount_code
          ? data.error.discount_code[0]
          : '';
        this.isDiscountLoading = false;
        delete this.printService.specs.discount_code;
      }
    );
  }

  getDeliveryDate() {
    return moment(this.order.delivery_option.estimated_delivery_date).toDate();
  }

  displayZipAndCity() {
    if (this.order.shipping_address.country_code === 'US') {
      return `${this.order.shipping_address.city}, ${this.order.shipping_address.state} ${this.order.shipping_address.zip_code}`;
    } else {
      return `${this.order.shipping_address.zip_code} ${this.order.shipping_address.city}`;
    }
  }

  displayCountry() {
    const code = this.order.shipping_address.country_code;
    return this.countriesService.get(code).name;
  }

  isOrderReady() {
    return (
      this.printService.specs.payment_method_id ||
      (this.order && !parseFloat(this.order.final_price))
    );
  }

  isDiscountExists() {
    return parseFloat(this.order.discount_price);
  }

  isTaxExists() {
    return parseFloat(this.order.tax_price);
  }

  formatNumber(number) {
    return formatNumber(number, 'International');
  }

  submitOrder() {
    this.isProcessing = true;
    this.printService.purchase().subscribe(
      data => {
        this.firebaseService.logEvent('checkout_purchase', {
          print_order_id: this.order.id,
          story_id: this.printService.storyId,
          project_id: this.printService.projectId,
          book_format_id: this.order.book_format.id,
          delivery_option_id: this.order.delivery_option.id,
          quantity: this.order.quantity,
          product_price: this.order.product_price,
          shipping_price: this.order.shipping_price,
          discount_price: this.order.discount_price,
          tax_price: this.order.tax_price,
          total_price: this.order.final_price,
          currency: this.order.currency,
        });
        this.facebookPixelService.trackEvent('Purchase', {
          discount_price: this.order.discount_price,
          product_price: this.order.product_price,
          quantity: this.order.quantity,
          shipping_price: this.order.shipping_price,
          tax_price: this.order.tax_price,
          total_price: this.order.final_price,
          currency: this.order.currency,
          value: this.order.final_price - this.order.tax_price,
        });
        this.creditCardStorageSettingsService.removeCards();
        this.setStep(6);
      },
      data => {
        this.isProcessing = false;
        const error = data.error;
        this.firebaseService.logEvent('checkout_purchase_error', {
          print_order_id: this.order.id,
          story_id: this.printService.storyId,
          project_id: this.printService.projectId,
          reason: error.detail,
        });
        if (error.detail) {
          this.toasterService.pop('error', undefined, error.detail);
        }
      }
    );
  }
}
